import { VStack, Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { CoinbaseLogoIcon, MetamaskFoxIcon, WalletConnectIcon } from '@/components/Icons/wallets';
import { BasicModalLayout } from '@/components/Modal/Parts';
import { useEthereumInjected } from '@/features/common/hooks/useEthereumInjected';

interface ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConnectModal = ({ isOpen, onClose }: ConnectModalProps) => {
  const { isConnected } = useAccount();
  const { connectors, connectAsync, isPending } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { t } = useTranslation('common');
  const isInjectedEthereum = useEthereumInjected();

  const onConnect = async (connectorId: string) => {
    try {
      if (isConnected) {
        await disconnectAsync();
      }

      const connector = connectors.find((connectorItem) => connectorItem.id === connectorId);

      if (!connector) {
        return;
      }

      await connectAsync({
        connector,
      });
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <BasicModalLayout
      title={t('common.components.elements.buttons.connect')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <VStack gap={4}>
        <Button
          display={isInjectedEthereum ? 'flex' : 'none'}
          onClick={() => onConnect('injected')}
          py={6}
          px={4}
          h={20}
          maxH={16}
          loading={isPending}
          disabled={isPending}
          gap={2}
          w="100%"
          color="brand.staticWhite"
          fontSize="16px"
          fontWeight="bold"
          letterSpacing="1px"
          borderRadius="8px"
          style={{
            background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
            border: 'none',
          }}
          _hover={{
            opacity: 0.8,
          }}
        >
          <MetamaskFoxIcon boxSize={9} />
          <Text as="span">{t('common.components.elements.buttons.metaMask')}</Text>
        </Button>
        <Button
          onClick={() => onConnect('coinbaseWalletSDK')}
          py={6}
          px={4}
          h={20}
          maxH={16}
          loading={isPending}
          disabled={isPending}
          gap={2}
          w="100%"
          color="brand.staticWhite"
          fontSize="16px"
          fontWeight="bold"
          letterSpacing="1px"
          borderRadius="8px"
          style={{
            background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
            border: 'none',
          }}
          _hover={{
            opacity: 0.8,
          }}
        >
          <CoinbaseLogoIcon boxSize={9} />
          <Text as="span">{t('common.components.elements.buttons.coinbase')}</Text>
        </Button>
        <Button
          onClick={() => onConnect('walletConnect')}
          py={6}
          px={4}
          h={20}
          maxH={16}
          loading={isPending}
          disabled={isPending}
          gap={2}
          w="100%"
          color="brand.staticWhite"
          fontSize="16px"
          fontWeight="bold"
          letterSpacing="1px"
          borderRadius="8px"
          style={{
            background: 'linear-gradient(to right, #29A3CC, #AD13CC)',
            border: 'none',
          }}
          _hover={{
            opacity: 0.8,
          }}
        >
          <WalletConnectIcon boxSize={9} />
          <Text as="span">{t('common.components.elements.buttons.walletConnect')}</Text>
        </Button>
      </VStack>
    </BasicModalLayout>
  );
};

export default ConnectModal;
